// Import Redux
import { Dispatch } from 'redux';
// Import Services
import { StringService } from '@app/services';
// Import Action Type Enumeration
import ActionType from '@app/redux/actions/types';
import { NotificationType } from '@app/enumerations';
// Import Api Models
import {
    MinCostPerSquareFoot,
    MinCostPerSquareFootWithId,
} from '@app/models/api';
// Import Controller Factory
import ControllerFactoryProvider from '@app/controllers/factory';
// Import Action Methods
import {
    addNotification,
    endProcessing,
    startProcessing,
} from '@app/redux/actions/methods';
// Import Actions.
import {
    MinCostPerSquareFootVersionAction,
} from '@app/redux/actions';
// Import Action Parameters
import {
    MinCostPerSquareFootVersionParameters,
    OverlayTargetParameters
} from './ActionParametersBarrel';

interface MinCostPerSquareFootParameters extends MinCostPerSquareFootVersionParameters {
    minCostPerSquareFootId: number;
}

interface MinCostPerSquareFootAction extends MinCostPerSquareFootVersionAction {
    minCostPerSquareFootId: number;
}

//#region Load Min Costs Per Square Foot Action.

interface LoadMinCostsPerSquareFootAction extends MinCostPerSquareFootVersionAction {
    minCostsPerSquareFoot: MinCostPerSquareFootWithId[];
}

type LoadMinCostPerSquareFootByVersionParameters = OverlayTargetParameters & MinCostPerSquareFootVersionParameters;

const loadMinCostsPerSquareFootAction: (minCostsPerSquareFoot: MinCostPerSquareFootWithId[], params: LoadMinCostPerSquareFootByVersionParameters) =>
    LoadMinCostsPerSquareFootAction = (minCostsPerSquareFoot: MinCostPerSquareFootWithId[], params: LoadMinCostPerSquareFootByVersionParameters) => ({
    ...params,
    minCostsPerSquareFoot,
    type: ActionType.LOAD_MINIMUM_COSTS_PER_SQUARE_FOOT,
});

const loadMinCostsPerSquareFoot = (params: LoadMinCostPerSquareFootByVersionParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootWithId[]> => {
        const messageId: string= `${StringService.getInstance().hashObject(params)}-minCostsPerSquareFoot`;
        try {
            await startProcessing({
                message: 'Loading Minimum Costs Per Square Foot table...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            const minCostsPerSquareFoot = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootController()
                .getAllMinCostPerSquareFootByVersionId(params.versionId);

            // Load to redux.
            dispatch<LoadMinCostsPerSquareFootAction>(loadMinCostsPerSquareFootAction(minCostsPerSquareFoot, params));

            await endProcessing({ 
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostsPerSquareFoot;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion Load Min Costs Per Square Foot Action

//#region Delete Min Cost Per Square Foot Action

type DeleteMinCostPerSquareFootParameters = MinCostPerSquareFootParameters & OverlayTargetParameters;

const deleteMinCostPerSquareFoot = (params: DeleteMinCostPerSquareFootParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootWithId[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-deleteMinCostPerSquareFoot`;
        try {
            await startProcessing({
                message: 'Removing Minimum Cost Per Square Foot Entry...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootController()
                .deleteMinCostPerSquareFoot(params.minCostPerSquareFootId);

            const minCostsPerSquareFoot = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootController()
                .getAllMinCostPerSquareFootByVersionId(params.versionId);
            // load to redux
            dispatch<LoadMinCostsPerSquareFootAction>(loadMinCostsPerSquareFootAction(minCostsPerSquareFoot, params));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostsPerSquareFoot;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion Delete Min Cost Per Square Foot Action

//#region Add Min Cost Per Square Foot Action

interface AddMinCostPerSquareFootParameters extends MinCostPerSquareFootVersionParameters, OverlayTargetParameters {
    minCostPerSquareFoot: MinCostPerSquareFoot;
}

const addMinCostPerSquareFoot = (params: AddMinCostPerSquareFootParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootWithId[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-addMinCostPerSquareFoot`;
        try {
            await startProcessing({
                message: 'Saving Minimum Cost Per Square Foot information...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootController()
                .addMinCostPerSquareFoot(
                    params.minCostPerSquareFoot
                );

            const minCostsPerSquareFoot = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootController()
                .getAllMinCostPerSquareFootByVersionId(params.versionId);

            dispatch<LoadMinCostsPerSquareFootAction>(loadMinCostsPerSquareFootAction(minCostsPerSquareFoot, { ...params }));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostsPerSquareFoot;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion Add Min Cost Per Square Foot Action

//#region Update Min Cost Per Square Foot

interface UpdateMinCostPerSquareFootParameters extends MinCostPerSquareFootParameters, OverlayTargetParameters {
    minCostPerSquareFoot: MinCostPerSquareFoot;
}

const updateMinCostPerSquareFoot = (params: UpdateMinCostPerSquareFootParameters) =>
    async (dispatch: Dispatch): Promise<MinCostPerSquareFootWithId[]> => {
        const messageId: string = `${StringService.getInstance().hashObject(params)}-updateMinCostPerSquareFoot`;
        try {
            await startProcessing({
                message: 'Saving Minimum Cost Per Square Foot information...',
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootController()
                .updateMinCostPerSquareFoot(
                    params.minCostPerSquareFootId,
                    params.minCostPerSquareFoot
                );

            const minCostsPerSquareFoot = await ControllerFactoryProvider
                .getControllerFactory()
                .getMinCostPerSquareFootController()
                .getAllMinCostPerSquareFootByVersionId(params.versionId);

            // Load to redux.
            dispatch<LoadMinCostsPerSquareFootAction>(loadMinCostsPerSquareFootAction(minCostsPerSquareFoot, {...params}));

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            return minCostsPerSquareFoot;
        } 
        catch (error: any) {
            await addNotification({
                title: 'Error',
                primaryMessage: error.message,
                secondaryMessage: error.systemMessage,
                notificationType: NotificationType.ERROR,
            })(dispatch);

            await endProcessing({
                messageId,
                overlayTarget: params.overlayTarget,
            })(dispatch);

            throw error;
        }
    };

//#endregion Update Min Cost Per Square Foot

//#region Select Min Cost Per Square Foot Action

type SelectMinCostPerSquareFootParameters = MinCostPerSquareFootParameters;

type SelectMinCostPerSquareFootAction = MinCostPerSquareFootAction;

const selectMinCostPerSquareFootAction: (params: SelectMinCostPerSquareFootParameters) =>
    SelectMinCostPerSquareFootAction = (params: SelectMinCostPerSquareFootParameters) => ({
        ...params,
        type: ActionType.SELECT_MINIMUM_COST_PER_SQUARE_FOOT,
    });

const selectMinCostPerSquareFoot = (params: SelectMinCostPerSquareFootParameters) =>
    async (dispatch: Dispatch): Promise<void> => {
        try {
            // Select on redux.
            dispatch<SelectMinCostPerSquareFootAction>(selectMinCostPerSquareFootAction(params));
        }
        catch (error: any) {
            throw error;
        }
    };

//#endregion Select Min Cost Per Square Foot Action

//#region Unselect Min Cost Per Square Foot Action

type UnselectMinCostPerSquareFootParameters = MinCostPerSquareFootVersionParameters;

type UnselectMinCostPerSquareFootAction = MinCostPerSquareFootVersionAction;

const unselectMinCostPerSquareFootAction: (params: UnselectMinCostPerSquareFootParameters) =>
    UnselectMinCostPerSquareFootAction = (params: UnselectMinCostPerSquareFootParameters) => ({
    ...params,
    type: ActionType.UNSELECT_MINIMUM_COST_PER_SQUARE_FOOT,
});

const unselectMinCostPerSquareFoot = (params: UnselectMinCostPerSquareFootParameters) =>
    async (dispatch: Dispatch): Promise<void> => {
        try {
            // Unselect on redux.
            dispatch<UnselectMinCostPerSquareFootAction>(unselectMinCostPerSquareFootAction(params));
        }
        catch (error: any) {
            throw error;
        }
    };

//#endregion Unselect Minimum Cost Per Square Foot Action

//#region Export actions.

export {
    MinCostPerSquareFootAction,
    LoadMinCostsPerSquareFootAction,
    SelectMinCostPerSquareFootAction,
    UnselectMinCostPerSquareFootAction,
};

//#endregion

//#region Export action methods.

export {
    addMinCostPerSquareFoot,
    deleteMinCostPerSquareFoot,
    loadMinCostsPerSquareFoot,
    selectMinCostPerSquareFoot,
    updateMinCostPerSquareFoot,
    unselectMinCostPerSquareFoot,
};

//#endregion

//#region Export action parameters.

export {
    MinCostPerSquareFootParameters,
    AddMinCostPerSquareFootParameters,
    DeleteMinCostPerSquareFootParameters,
    UpdateMinCostPerSquareFootParameters,
    LoadMinCostPerSquareFootByVersionParameters,
    SelectMinCostPerSquareFootParameters,
    UnselectMinCostPerSquareFootParameters,
};

//#endregion

//#region Export action factories.

export {
    loadMinCostsPerSquareFootAction,
    selectMinCostPerSquareFootAction,
    unselectMinCostPerSquareFootAction,
};

//#endregion
